import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import EventForm from "@/components/EventForm";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, CalendarPlus, ChevronUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All events" },
  { key: "special", label: "Special" },
  { key: "community", label: "Community" },
  { key: "church", label: "Church" },
];

export default function Events() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(params.get("post") === "1");
  const { user } = useAuth();
  const nav = useNavigate();
  const category = params.get("category") || "all";
  const q = params.get("q") || "";

  const loadEvents = useCallback(() => {
    setLoading(true);
    const query = { upcoming: true, limit: 48 };
    if (category !== "all") query.category = category;
    if (q) query.q = q;
    return api.get("/events", { params: query })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, [category, q]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "all") p.delete(k); else p.set(k, v);
    setParams(p);
  };

  const openForm = () => {
    if (!user) {
      toast.info("Sign in to post an event");
      nav("/login?redirect=/events?post=1");
      return;
    }
    setFormOpen(true);
    // Smooth scroll after mount
    setTimeout(() => {
      document.getElementById("events-inline-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const closeForm = () => {
    setFormOpen(false);
    const p = new URLSearchParams(params);
    p.delete("post");
    setParams(p);
  };

  const onEventSaved = async (ev) => {
    await loadEvents();
    closeForm();
    // Jump to the newly created event so the poster sees it live
    nav(`/events/${ev.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Events</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1E3A2F] mt-1">Gather with the community</h1>
          <p className="text-[#5C5C5C] mt-2 max-w-2xl">Special occasions, community meetups and church gatherings across Washington.</p>
        </div>
        <button
          type="button"
          onClick={formOpen ? closeForm : openForm}
          data-testid="events-post-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full abm-btn-primary h-11 px-5 text-white text-sm font-medium shrink-0"
        >
          {formOpen ? (<><ChevronUp className="w-4 h-4" /> Hide form</>) : (<><CalendarPlus className="w-4 h-4" /> Post an event</>)}
        </button>
      </div>

      {formOpen && user && (
        <section id="events-inline-form" className="mb-10" data-testid="events-inline-form">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">New event</div>
            <h2 className="font-display text-3xl text-[#1E3A2F] mt-1">Tell the community about your event</h2>
            <p className="text-sm text-[#5C5C5C] mt-1">Fill this in — it will show up on the Events page right after you publish.</p>
          </div>
          <EventForm onSaved={onEventSaved} onCancel={closeForm} submitLabel="Publish event" />
        </section>
      )}

      <div className="rounded-2xl bg-white border border-[#E2DED0] p-4 mb-8 flex flex-col md:flex-row md:items-center gap-3">
        <Tabs value={category} onValueChange={(v) => setParam("category", v)} className="w-full md:w-auto">
          <TabsList data-testid="events-tabs" className="bg-[#F7F4EB]">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`events-tab-${t.key}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 bg-[#F7F4EB] rounded-full px-3 md:ml-auto flex-1">
          <Search className="w-4 h-4 text-[#5C5C5C]" />
          <Input
            data-testid="events-search-input"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
            placeholder="Search events…"
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#5C5C5C]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
          <div className="font-display text-2xl text-[#1E3A2F]">No upcoming events yet</div>
          <p className="text-sm text-[#5C5C5C] mt-2">Be the one who brings the community together.</p>
          <button
            type="button"
            onClick={openForm}
            data-testid="events-empty-post-btn"
            className="inline-flex items-center gap-2 mt-5 rounded-full abm-btn-primary h-10 px-5 text-white text-sm font-medium"
          >
            <CalendarPlus className="w-4 h-4" /> Post an event
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((e) => <EventCard key={e.id} event={e} />)}
        </div>
      )}
    </div>
  );
}
